// @ts-nocheck
/**
 * Curated form section controller.
 *
 * Reads offering & datepicker config carriers (rendered by `_curated-offering`,
 * `_curated-offering-option`, `_curated-offering-box`, `_curated-datepicker`,
 * `_curated-disabled-date`, `curated-pickup-window` blocks) and builds an interactive form:
 *   - available offerings checkbox list + add button
 *   - selected offerings list with box-size, options, qty, disclaimer
 *   - flatpickr-driven pickup date with lead time, EST cutoff, disabled weekdays / dates
 *   - day-specific pickup time windows (Breakfast / Lunch / Brunch / Dinner)
 *   - submit gated by validation
 */
(function () {
  'use strict';

  const SVG_NS = 'http://www.w3.org/2000/svg';

  /** @param {string} dateStr e.g. "06/15/2026" with possible whitespace */
  function parseAdminDate(dateStr) {
    if (!dateStr) return null;
    const trimmed = String(dateStr).replace(/\s+/g, '').trim();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10) - 1;
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    const d = new Date(year, month, day);
    if (
      d.getFullYear() !== year ||
      d.getMonth() !== month ||
      d.getDate() !== day
    ) {
      return null;
    }
    return d;
  }

  function uid(prefix) {
    return prefix + '-' + Math.random().toString(36).slice(2, 9);
  }

  /**
   * Parse an "HH:MM" 24-hour time string into total minutes from midnight.
   * Returns null when malformed.
   */
  function parseTimeOfDay(value) {
    if (!value) return null;
    const trimmed = String(value).trim();
    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  }

  /** Format minutes-from-midnight into the requested display format. */
  function formatTimeOfDay(totalMinutes, format) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const mm = String(minutes).padStart(2, '0');
    if (format === '24') {
      return `${String(hours).padStart(2, '0')}:${mm}`;
    }
    const period = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours % 12;
    if (displayHour === 0) displayHour = 12;
    return `${displayHour}:${mm} ${period}`;
  }

  /**
   * Build the ordered list of pickup-time option strings from the configured
   * start, end and interval. End time is inclusive when it falls on the grid.
   */
  function buildTimeOptions(cfg) {
    const start = parseTimeOfDay(cfg.start);
    const end = parseTimeOfDay(cfg.end);
    const interval = parseInt(cfg.interval, 10);
    if (start == null || end == null) return [];
    if (!interval || interval <= 0) return [];
    if (end < start) return [];
    const options = [];
    for (let t = start; t <= end; t += interval) {
      options.push(formatTimeOfDay(t, cfg.format));
    }
    return options;
  }

  /**
   * Read calendar date + clock parts for a Date in a given IANA timezone.
   */
  function getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type) => {
      const part = parts.find((p) => p.type === type);
      return part ? parseInt(part.value, 10) : 0;
    };
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour') % 24,
      minute: get('minute'),
    };
  }

  /**
   * Earliest selectable pickup calendar date from lead hours + daily cutoff.
   * Before cutoff: today (in TZ) + leadDays.
   * On/after cutoff: today (in TZ) + leadDays + 1.
   */
  function computeMinPickupDate(leadHours, cutoffHHMM, timeZone) {
    const hours = parseInt(leadHours, 10);
    const leadDays = !isNaN(hours) && hours > 0 ? Math.ceil(hours / 24) : 0;
    const zoned = getZonedParts(new Date(), timeZone);
    const cutoffMinutes = parseTimeOfDay(cutoffHHMM);
    const nowMinutes = zoned.hour * 60 + zoned.minute;
    let days = leadDays;
    if (cutoffMinutes != null && nowMinutes >= cutoffMinutes) {
      days += 1;
    }
    // Flatpickr compares Date objects in the browser's local calendar.
    const min = new Date(zoned.year, zoned.month - 1, zoned.day);
    min.setDate(min.getDate() + days);
    min.setHours(0, 0, 0, 0);
    return min;
  }

  /**
   * Build time options for a specific calendar date from pickup windows.
   * Falls back to the legacy single-range config when no windows exist.
   * Returns [{ value, label, group, minutes }] sorted chronologically.
   */
  function buildTimeOptionsForDate(date, windows, format, fallbackCfg) {
    if (!windows || !windows.length) {
      return buildTimeOptions(fallbackCfg).map((t) => ({
        value: t,
        label: t,
        group: '',
        minutes: 0,
      }));
    }
    if (!date) return [];

    const day = date.getDay();
    const matching = windows.filter((w) => w.days.indexOf(day) !== -1);
    const byMinutes = new Map();

    matching.forEach((w) => {
      const start = parseTimeOfDay(w.start);
      const end = parseTimeOfDay(w.end);
      const interval = parseInt(w.interval, 10);
      if (start == null || end == null || !interval || interval <= 0 || end < start) {
        return;
      }
      for (let t = start; t <= end; t += interval) {
        if (!byMinutes.has(t)) {
          byMinutes.set(t, {
            value: formatTimeOfDay(t, format),
            label: formatTimeOfDay(t, format),
            group: w.label || '',
            minutes: t,
          });
        }
      }
    });

    return Array.from(byMinutes.values()).sort((a, b) => a.minutes - b.minutes);
  }

  /** Weekdays (0–6) that have at least one configured pickup window. */
  function weekdaysWithWindows(windows) {
    const set = new Set();
    (windows || []).forEach((w) => {
      (w.days || []).forEach((d) => set.add(d));
    });
    return set;
  }

  function optionName(option) {
    return option && option.name ? option.name : '';
  }

  function parseDollarAmount(value) {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || !isFinite(parsed)) return 0;
    return Math.max(0, parsed);
  }

  function formatDollarAmount(value) {
    return `$${value.toFixed(2)}`;
  }

  class CuratedForm extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.showOptionImages = this.dataset.showOptionImages !== 'false';
      this.offerings = this._readOfferings();
      this.pickupLocationsConfig = this._readPickupLocations();
      this.datepickerConfig = this._readDatepicker();

      this.selections = []; // [{ uid, offeringId, boxSize, optionValues:[], qty }]
      this.checkedOfferingIds = new Set();

      this.els = {
        availableList: this.querySelector('[data-curated-available]'),
        addButton: this.querySelector('[data-curated-add]'),
        selectedList: this.querySelector('[data-curated-selected]'),
        emptyMsg: this.querySelector('[data-curated-empty]'),
        pickupLocationMount: this.querySelector(
          '[data-curated-pickup-location-mount]',
        ),
        datepickerMount: this.querySelector('[data-curated-datepicker-mount]'),
        nameInput: this.querySelector('[data-curated-name]'),
        emailInput: this.querySelector('[data-curated-email]'),
        phoneInput: this.querySelector('[data-curated-phone]'),
        subtotalValue: this.querySelector('[data-curated-subtotal-value]'),
        error: this.querySelector('[data-curated-error]'),
        submit: this.querySelector('[data-curated-submit]'),
        form: this.querySelector('[data-curated-form]'),
        storeify: this.querySelector('[data-curated-storeify-form]'),
      };

      this._state = 'editing';
      this.dataset.state = this._state;

      this._renderAvailable();
      this._renderPickupLocations();
      this._renderDatepicker();

      this.els.addButton.addEventListener('click', () => this._addSelected());
      this.els.form.addEventListener('submit', (e) => this._onSubmit(e));
      if (this.els.nameInput) {
        this.els.nameInput.addEventListener('input', () => this._refreshState());
      }
      if (this.els.emailInput) {
        this.els.emailInput.addEventListener('input', () => this._refreshState());
      }
      if (this.els.phoneInput) {
        this.els.phoneInput.addEventListener('input', () => this._refreshState());
      }

      this._refreshState();
    }

    /** Read offering configs from <template data-curated-offering> elements. */
    _readOfferings() {
      const templates = this.querySelectorAll('[data-curated-offering]');
      const offerings = [];
      templates.forEach((tpl) => {
        const ds = tpl.dataset;
        const content = tpl.content || tpl;
        const options = Array.from(
          content.querySelectorAll('[data-curated-option]'),
        )
          .map((el) => ({
            name: (el.dataset.name || '').trim(),
            image: el.dataset.image || '',
            imageAlt: el.dataset.imageAlt || el.dataset.name || '',
          }))
          .filter((opt) => !!opt.name);
        const boxes = Array.from(
          content.querySelectorAll('[data-curated-box]'),
        )
          .map((el) => ({
            size: parseInt(el.dataset.size, 10) || 1,
            label: el.dataset.label || '',
          }))
          .filter((b) => b.size > 0);

        offerings.push({
          id: ds.blockId,
          name: ds.name || 'Offering',
          image: ds.image || '',
          imageAlt: ds.imageAlt || ds.name || '',
          description: ds.description || '',
          disclaimer: ds.disclaimer || '',
          price: parseDollarAmount(ds.price),
          enableSpecialPackaging: ds.enableSpecialPackaging === 'true',
          specialPackagingText: ds.specialPackagingText || 'Request special packaging',
          specialPackagingPrice: parseDollarAmount(ds.specialPackagingPrice),
          quantityLimit: parseInt(ds.quantityLimit, 10) || 0,
          allowDuplicates: ds.allowDuplicates === 'true',
          options,
          boxes,
        });
      });
      return offerings;
    }

    _findOptionByName(offering, name) {
      if (!offering || !name) return null;
      return offering.options.find((opt) => optionName(opt) === name) || null;
    }

    _selectionUnitPrice(sel, offering) {
      if (!offering) return 0;
      const base = offering.price || 0;
      const packaging =
        offering.enableSpecialPackaging && sel.specialPackagingRequested
          ? offering.specialPackagingPrice || 0
          : 0;
      return base + packaging;
    }

    _selectionLineTotal(sel, offering) {
      return this._selectionUnitPrice(sel, offering) * (sel.qty || 1);
    }

    _subtotal() {
      return this.selections.reduce((sum, sel) => {
        const offering = this.offerings.find((o) => o.id === sel.offeringId);
        return sum + this._selectionLineTotal(sel, offering);
      }, 0);
    }

    _updateSubtotal() {
      if (!this.els.subtotalValue) return;
      this.els.subtotalValue.textContent = formatDollarAmount(this._subtotal());
    }

    _renderOptionImage(imgHolder, option) {
      if (!imgHolder) return;
      imgHolder.innerHTML = '';
      if (!option || !option.image) return;
      const img = document.createElement('img');
      img.className = 'curated-form__option-image';
      img.src = option.image;
      img.alt = option.imageAlt || optionName(option);
      img.loading = 'lazy';
      imgHolder.appendChild(img);
    }

    /** Read pickup-location config and nested locations from its static block. */
    _readPickupLocations() {
      const tpl = this.querySelector('[data-curated-pickup-locations]');
      if (!tpl) return null;
      const ds = tpl.dataset;
      const content = tpl.content || tpl;
      const locations = Array.from(
        content.querySelectorAll('[data-curated-pickup-location]'),
      )
        .map((el) => ({
          name: (el.dataset.name || '').trim(),
          value: (el.dataset.value || el.dataset.name || '').trim(),
        }))
        .filter((loc) => !!loc.name);

      return {
        title: ds.title || 'Pickup location',
        placeholder: ds.placeholder || 'Select a pickup location',
        disclaimer: ds.disclaimer || '',
        locations,
      };
    }

    _renderPickupLocations() {
      const cfg = this.pickupLocationsConfig;
      const mount = this.els.pickupLocationMount;
      this._pickupLocationSelect = null;
      if (!cfg || !mount) return;

      const selectId = uid('curated-pickup-location');
      const lbl = document.createElement('label');
      lbl.className = 'curated-form__label';
      lbl.htmlFor = selectId;
      lbl.textContent = cfg.title;

      const select = document.createElement('select');
      select.id = selectId;
      select.name = 'pickup_location';
      select.className = 'curated-form__select';
      select.required = true;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = cfg.placeholder;
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      cfg.locations.forEach((loc) => {
        const opt = document.createElement('option');
        opt.value = loc.value;
        opt.textContent = loc.name;
        select.appendChild(opt);
      });

      select.addEventListener('change', () => this._refreshState());

      mount.appendChild(lbl);
      mount.appendChild(select);

      if (cfg.disclaimer) {
        const disc = document.createElement('div');
        disc.className = 'curated-form__hint';
        disc.innerHTML = cfg.disclaimer;
        mount.appendChild(disc);
      }

      this._pickupLocationSelect = select;
    }

    /** Read the (single) datepicker config + its disabled dates + pickup windows. */
    _readDatepicker() {
      const tpl = this.querySelector('[data-curated-datepicker]');
      if (!tpl) return null;
      const ds = tpl.dataset;
      const content = tpl.content || tpl;
      const disabledDates = Array.from(
        content.querySelectorAll('[data-curated-disabled-date]'),
      )
        .map((el) => {
          const mode = el.dataset.mode;
          if (mode === 'range') {
            const from = parseAdminDate(el.dataset.from);
            const to = parseAdminDate(el.dataset.to);
            if (!from || !to) return null;
            return { from, to };
          }
          const date = parseAdminDate(el.dataset.date);
          if (!date) return null;
          return date;
        })
        .filter(Boolean);

      const disabledWeekdays = (ds.disabledWeekdays || '')
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));

      const windows = Array.from(
        content.querySelectorAll('[data-curated-pickup-window]'),
      )
        .map((el) => {
          const days = (el.dataset.days || '')
            .split(',')
            .map((s) => parseInt(s, 10))
            .filter((n) => !isNaN(n));
          return {
            label: el.dataset.label || '',
            days,
            start: el.dataset.start || '',
            end: el.dataset.end || '',
            interval: el.dataset.interval || '30',
          };
        })
        .filter((w) => w.days.length && w.start && w.end);

      const timeFormat = ds.timeFormat === '24' ? '24' : '12';
      const timeConfig = {
        enabled: ds.enableTime === 'true',
        title: ds.timeTitle || 'Pickup time',
        placeholder: ds.timePlaceholder || 'Select a time',
        start: ds.timeStart || '',
        end: ds.timeEnd || '',
        interval: ds.timeInterval || '',
        format: timeFormat,
        windows,
      };
      // Legacy flat options (used only when no windows are configured).
      timeConfig.options = timeConfig.enabled && !windows.length
        ? buildTimeOptions(timeConfig)
        : [];

      const leadHours = parseInt(ds.leadHours, 10);
      const cutoffTime = ds.cutoffTime || '15:00';
      const cutoffTimezone = ds.cutoffTimezone || 'America/New_York';

      return {
        title: ds.title || 'Pickup date',
        placeholder: ds.placeholder || 'Select a date',
        disclaimer: ds.disclaimer || '',
        disabledWeekdays,
        disabledDates,
        leadHours: !isNaN(leadHours) && leadHours > 0 ? leadHours : 48,
        cutoffTime,
        cutoffTimezone,
        time: timeConfig,
      };
    }

    _totalQtyForOffering(offeringId) {
      return this.selections
        .filter((s) => s.offeringId === offeringId)
        .reduce((sum, s) => sum + (s.qty || 1), 0);
    }

    _isOfferingFull(offering) {
      if (!offering.quantityLimit || offering.quantityLimit <= 0) return false;
      return this._totalQtyForOffering(offering.id) >= offering.quantityLimit;
    }

    /** Render the checkbox list of available offerings. */
    _renderAvailable() {
      const list = this.els.availableList;
      list.innerHTML = '';
      this.offerings.forEach((offering) => {
        const li = document.createElement('li');
        li.className = 'curated-form__available-item';
        li.dataset.offeringId = offering.id;

        const labelId = uid('curated-offering-label');

        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = labelId;
        cb.value = offering.id;
        cb.addEventListener('change', () => {
          if (cb.checked) this.checkedOfferingIds.add(offering.id);
          else this.checkedOfferingIds.delete(offering.id);
          this._refreshState();
        });
        li.appendChild(cb);

        const text = document.createElement('label');
        text.className = 'curated-form__available-item-holder';
        text.htmlFor = labelId;
        const imgHolder = document.createElement('div');
        imgHolder.className = 'curated-form__image-holder';
        if (offering.image) {
          const img = document.createElement('img');
          img.src = offering.image;
          img.alt = offering.imageAlt || '';
          img.className = 'curated-form__image';
          img.loading = 'lazy';
          imgHolder.appendChild(img);
        }
        text.appendChild(imgHolder);
        const name = document.createElement('span');
        name.className = 'curated-form__available-name';
        name.textContent = offering.name + (((offering.price || 0) > 0) ? ` – ` : '');
        if ((offering.price || 0) > 0) {
          const price = document.createElement('strong');
          price.className = 'curated-form__available-price';
          price.textContent = `${formatDollarAmount(offering.price)}`;
          name.appendChild(price);
        }
        text.appendChild(name);
        if (offering.description) {
          const desc = document.createElement('span');
          desc.className = 'curated-form__available-desc';
          desc.innerHTML = offering.description;
          text.appendChild(desc);
        }
        li.appendChild(text);

        list.appendChild(li);
      });
    }

    /** Push currently-checked offerings into the selection list. */
    _addSelected() {
      this.checkedOfferingIds.forEach((offeringId) => {
        const offering = this.offerings.find((o) => o.id === offeringId);
        if (!offering) return;
        if (this._isOfferingFull(offering)) return;
        const defaultBox =
          offering.boxes && offering.boxes.length ? offering.boxes[0] : null;
        const boxSize = defaultBox ? defaultBox.size : 1;
        this.selections.unshift({
          uid: uid('sel'),
          offeringId,
          boxSize,
          optionValues: new Array(boxSize).fill(''),
          qty: 1,
          specialPackagingRequested: false,
          specialPackagingInstructions: '',
        });
      });
      this.checkedOfferingIds.clear();
      this.els.availableList
        .querySelectorAll('input[type="checkbox"]')
        .forEach((cb) => (cb.checked = false));
      this._renderSelected();
      this._refreshState();
    }

    _removeSelection(selUid) {
      this.selections = this.selections.filter((s) => s.uid !== selUid);
      this._renderSelected();
      this._refreshState();
    }

    _renderSelected() {
      const list = this.els.selectedList;
      // Drop everything except the empty placeholder
      Array.from(list.children).forEach((c) => {
        if (c !== this.els.emptyMsg) c.remove();
      });
      this.els.emptyMsg.style.display = this.selections.length ? 'none' : '';

      this.selections.forEach((sel) => {
        const offering = this.offerings.find((o) => o.id === sel.offeringId);
        if (!offering) return;
        list.appendChild(this._renderSelectionItem(sel, offering));
      });
    }

    /**
     * Re-render only the <li> for a single selection. Used when state changes
     * (size, option) on one item so the rest of the list, scroll position and
     * focus elsewhere are preserved.
     */
    _rerenderSelectionItem(sel) {
      const offering = this.offerings.find((o) => o.id === sel.offeringId);
      if (!offering) return;
      const existing = this.els.selectedList.querySelector(
        `li[data-uid="${sel.uid}"]`,
      );
      const fresh = this._renderSelectionItem(sel, offering);
      if (existing && existing.parentNode) {
        existing.parentNode.replaceChild(fresh, existing);
      } else {
        this.els.selectedList.appendChild(fresh);
      }
    }

    _renderSelectionItem(sel, offering) {
      const li = document.createElement('li');
      li.className = 'curated-form__selected-item';
      li.dataset.uid = sel.uid;

      // Header: title + remove
      const header = document.createElement('div');
      header.className = 'curated-form__selected-header';
      const title = document.createElement('h3');
      title.className = 'curated-form__selected-title';
      title.textContent = offering.name + (((offering.price || 0) > 0) ? ` – ` : '');
      const titlePrice = document.createElement('strong');
      titlePrice.className = 'curated-form__title-price';
      title.appendChild(titlePrice);
      const updateTitlePrice = () => {
        const lineTotal = this._selectionLineTotal(sel, offering);
        titlePrice.textContent = lineTotal > 0 ? `${formatDollarAmount(lineTotal)}` : '';
      };
      updateTitlePrice();
      header.appendChild(title);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'curated-form__remove';
      remove.textContent = 'Remove';
      remove.addEventListener('click', () => this._removeSelection(sel.uid));
      header.appendChild(remove);
      li.appendChild(header);

      // Box-size selector — hidden if only one box configured
      if (offering.boxes.length > 1) {
        const row = document.createElement('div');
        row.className = 'curated-form__selected-row';
        const lbl = document.createElement('label');
        lbl.className = 'curated-form__label';
        lbl.textContent = 'Size';
        const selectId = uid('curated-box');
        lbl.htmlFor = selectId;
        const select = document.createElement('select');
        select.className = 'curated-form__select';
        select.id = selectId;
        offering.boxes.forEach((box) => {
          const opt = document.createElement('option');
          opt.value = String(box.size);
          opt.textContent = box.label || box.size;
          if (box.size === sel.boxSize) opt.selected = true;
          select.appendChild(opt);
        });
        select.addEventListener('change', () => {
          sel.boxSize = parseInt(select.value, 10) || 1;
          sel.optionValues = new Array(sel.boxSize).fill('');
          this._rerenderSelectionItem(sel);
          this._refreshState();
        });
        row.appendChild(lbl);
        row.appendChild(select);
        li.appendChild(row);
      } else if (offering.boxes.length === 1) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = `selections[${sel.uid}][box_size]`;
        hidden.value = String(sel.boxSize);
        li.appendChild(hidden);
      }

      // Per-box option dropdowns
      if (sel.boxSize > 0 && offering.options.length > 0) {
        const optsWrap = document.createElement('div');
        optsWrap.className = 'curated-form__selected-options';
        for (let i = 0; i < sel.boxSize; i++) {
          const field = document.createElement('div');
          field.className = 'curated-form__field';
          const lbl = document.createElement('label');
          lbl.className = 'curated-form__label';
          lbl.textContent = `Option ${i + 1}`;
          const selectId = uid('curated-opt');
          lbl.htmlFor = selectId;
          let imgHolder = null;
          if (this.showOptionImages) {
            imgHolder = document.createElement('div');
            imgHolder.className = 'curated-form__option-image-holder';
            this._renderOptionImage(
              imgHolder,
              this._findOptionByName(offering, sel.optionValues[i]),
            );
          }
          const select = document.createElement('select');
          select.className = 'curated-form__select';
          select.id = selectId;
          select.required = true;
          select.dataset.optionIndex = String(i);

          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = 'Select...';
          placeholder.disabled = true;
          placeholder.selected = !sel.optionValues[i];
          select.appendChild(placeholder);

          offering.options.forEach((option) => {
            const optName = optionName(option);
            if (!optName) return;
            const opt = document.createElement('option');
            opt.value = optName;
            opt.textContent = optName;
            if (sel.optionValues[i] === optName) opt.selected = true;
            // Disable duplicates if not allowed
            if (!offering.allowDuplicates) {
              const usedElsewhere = sel.optionValues.some(
                (v, idx) => idx !== i && v === optName,
              );
              if (usedElsewhere) opt.disabled = true;
            }
            select.appendChild(opt);
          });

          select.addEventListener('change', () => {
            sel.optionValues[i] = select.value;
            if (imgHolder) {
              this._renderOptionImage(
                imgHolder,
                this._findOptionByName(offering, select.value),
              );
            }
            // Re-render only this item to refresh duplicate-disabled state on
            // sibling dropdowns without disturbing scroll/focus elsewhere.
            if (!offering.allowDuplicates) this._rerenderSelectionItem(sel);
            this._refreshState();
          });

          field.appendChild(lbl);
          if (imgHolder) field.appendChild(imgHolder);
          field.appendChild(select);
          optsWrap.appendChild(field);
        }
        li.appendChild(optsWrap);
      }

      // Quantity selector (hidden if quantityLimit === 1)
      if (offering.quantityLimit !== 1) {
        const row = document.createElement('div');
        row.className = 'curated-form__selected-row';
        const lbl = document.createElement('span');
        lbl.className = 'curated-form__label';
        lbl.textContent = 'Quantity';
        row.appendChild(lbl);

        const qtyWrap = document.createElement('div');
        qtyWrap.className = 'curated-form__qty';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'curated-form__qty-button';
        minus.textContent = '\u2212';
        minus.setAttribute('aria-label', 'Decrease quantity');
        const input = document.createElement('input');
        input.type = 'number';
        input.className = 'curated-form__qty-input';
        input.min = '1';
        input.value = String(sel.qty);
        input.setAttribute('aria-label', 'Quantity');
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'curated-form__qty-button';
        plus.textContent = '+';
        plus.setAttribute('aria-label', 'Increase quantity');

        const setQty = (next) => {
          const limit = offering.quantityLimit;
          let max = Infinity;
          if (limit > 0) {
            const otherTotal =
              this._totalQtyForOffering(offering.id) - sel.qty;
            max = Math.max(1, limit - otherTotal);
          }
          next = Math.max(1, Math.min(next, max));
          sel.qty = next;
          input.value = String(next);
          minus.disabled = next <= 1;
          plus.disabled = next >= max;
          updateTitlePrice();
          this._refreshState();
        };

        minus.addEventListener('click', () => setQty(sel.qty - 1));
        plus.addEventListener('click', () => setQty(sel.qty + 1));
        input.addEventListener('change', () => {
          const parsed = parseInt(input.value, 10);
          setQty(isNaN(parsed) ? 1 : parsed);
        });

        qtyWrap.appendChild(minus);
        qtyWrap.appendChild(input);
        qtyWrap.appendChild(plus);
        row.appendChild(qtyWrap);
        li.appendChild(row);

        // Initialize disabled state
        setQty(sel.qty);
      }

      // Optional special packaging request
      if (offering.enableSpecialPackaging) {
        const row = document.createElement('div');
        row.className = 'curated-form__selected-row';

        const toggleId = uid('curated-special-packaging');
        const toggleWrap = document.createElement('div');
        toggleWrap.className = 'curated-form__field curated-form__field--checkbox';

        const toggle = document.createElement('input');
        toggle.type = 'checkbox';
        toggle.id = toggleId;
        toggle.checked = !!sel.specialPackagingRequested;

        const toggleLabel = document.createElement('label');
        toggleLabel.className = 'curated-form__label';
        toggleLabel.htmlFor = toggleId;
        toggleLabel.textContent = offering.specialPackagingText || 'Request special packaging';

        toggleWrap.appendChild(toggle);
        toggleWrap.appendChild(toggleLabel);
        row.appendChild(toggleWrap);

        const detailsWrap = document.createElement('div');
        detailsWrap.className = 'curated-form__field';
        detailsWrap.style.display = toggle.checked ? '' : 'none';

        const detailsLabel = document.createElement('label');
        detailsLabel.className = 'curated-form__label';
        const detailsId = uid('curated-special-packaging-notes');
        detailsLabel.htmlFor = detailsId;
        detailsLabel.textContent = 'Special packaging instructions';

        const details = document.createElement('textarea');
        details.id = detailsId;
        details.className = 'curated-form__input';
        details.rows = 3;
        details.placeholder = 'Enter special packaging instructions';
        details.value = sel.specialPackagingInstructions || '';
        details.required = toggle.checked;

        details.addEventListener('input', () => {
          sel.specialPackagingInstructions = details.value;
          this._refreshState();
        });

        toggle.addEventListener('change', () => {
          sel.specialPackagingRequested = toggle.checked;
          detailsWrap.style.display = toggle.checked ? '' : 'none';
          details.required = toggle.checked;
          updateTitlePrice();
          this._refreshState();
        });

        detailsWrap.appendChild(detailsLabel);
        detailsWrap.appendChild(details);
        row.appendChild(detailsWrap);
        li.appendChild(row);
      }

      // Per-offering disclaimer
      if (offering.disclaimer) {
        const disc = document.createElement('div');
        disc.className = 'curated-form__hint';
        disc.innerHTML = offering.disclaimer;
        li.appendChild(disc);
      }

      return li;
    }

    _renderDatepicker() {
      if (!this.datepickerConfig) return;
      const cfg = this.datepickerConfig;
      const mount = this.els.datepickerMount;

      const inputId = uid('curated-date');
      const lbl = document.createElement('label');
      lbl.className = 'curated-form__label';
      lbl.htmlFor = inputId;
      lbl.textContent = cfg.title;

      const input = document.createElement('input');
      input.type = 'text';
      input.id = inputId;
      input.name = 'pickup_date';
      input.className = 'curated-form__input';
      input.placeholder = cfg.placeholder;
      input.required = true;
      input.autocomplete = 'off';

      mount.appendChild(lbl);
      mount.appendChild(input);

      this._renderPickupTime(mount);

      if (cfg.disclaimer) {
        const disc = document.createElement('div');
        disc.className = 'curated-form__hint';
        disc.innerHTML = cfg.disclaimer;
        mount.appendChild(disc);
      }

      const minDate = computeMinPickupDate(
        cfg.leadHours,
        cfg.cutoffTime,
        cfg.cutoffTimezone,
      );
      const windowWeekdays = weekdaysWithWindows(
        cfg.time && cfg.time.windows ? cfg.time.windows : [],
      );

      const initFlatpickr = () => {
        if (typeof window.flatpickr !== 'function') return;
        const disable = [];
        cfg.disabledDates.forEach((d) => disable.push(d));
        disable.push((date) => {
          const day = date.getDay();
          if (cfg.disabledWeekdays.includes(day)) return true;
          // If pickup windows are configured, block days with no windows.
          if (windowWeekdays.size && !windowWeekdays.has(day)) return true;
          return false;
        });
        window.flatpickr(input, {
          dateFormat: 'm/d/Y',
          minDate,
          disable,
          allowInput: false,
          static: false,
          position: 'auto',
          onReady: (_, __, fp) => {
            fp.calendarContainer.classList.add('curated-form__calendar');
          },
          onChange: (selectedDates) => {
            this._refreshPickupTimeOptions(selectedDates[0] || null);
            this._refreshState();
          },
        });
        this._datepickerInput = input;
        input.addEventListener('change', () => this._refreshState());

        // Legacy mode (no windows): populate times immediately.
        if (cfg.time && cfg.time.enabled && !(cfg.time.windows || []).length) {
          this._refreshPickupTimeOptions(null);
        }
      };

      if (typeof window.flatpickr === 'function') {
        initFlatpickr();
      } else {
        // flatpickr.js is loaded with `defer`; wait for it.
        window.addEventListener('load', initFlatpickr, { once: true });
      }
    }

    /**
     * Render the optional pickup-time `<select>` into its own
     * `.curated-form__field` sibling immediately after the datepicker mount.
     * Options are filled by `_refreshPickupTimeOptions` once a date is chosen
     * (or immediately in legacy single-range mode).
     */
    _renderPickupTime(mount) {
      const cfg = this.datepickerConfig && this.datepickerConfig.time;
      this._pickupTimeSelect = null;
      this._pickupTimeField = null;
      if (!cfg || !cfg.enabled) return;

      const hasWindows = !!(cfg.windows && cfg.windows.length);
      const hasLegacyOptions = !!(cfg.options && cfg.options.length);
      if (!hasWindows && !hasLegacyOptions) return;

      const field = document.createElement('div');
      field.className = 'curated-form__field';
      field.dataset.curatedPickupTime = '';

      const selectId = uid('curated-time');
      const lbl = document.createElement('label');
      lbl.className = 'curated-form__label';
      lbl.htmlFor = selectId;
      lbl.textContent = cfg.title;

      const select = document.createElement('select');
      select.id = selectId;
      select.name = 'pickup_time';
      select.className = 'curated-form__select';
      select.required = true;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = hasWindows
        ? 'Select a date first'
        : cfg.placeholder;
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      if (hasWindows) {
        select.disabled = true;
      }

      select.addEventListener('change', () => this._refreshState());

      field.appendChild(lbl);
      field.appendChild(select);

      if (mount.parentNode) {
        mount.parentNode.insertBefore(field, mount.nextSibling);
      } else {
        mount.appendChild(field);
      }
      this._pickupTimeSelect = select;
      this._pickupTimeField = field;
    }

    /**
     * Rebuild pickup-time options for the selected date.
     * With windows: options depend on weekday. Without: use legacy flat list.
     */
    _refreshPickupTimeOptions(selectedDate) {
      const cfg = this.datepickerConfig && this.datepickerConfig.time;
      const select = this._pickupTimeSelect;
      if (!cfg || !cfg.enabled || !select) return;

      const previousValue = select.value;
      const hasWindows = !!(cfg.windows && cfg.windows.length);

      let options = [];
      if (hasWindows) {
        if (!selectedDate) {
          select.innerHTML = '';
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = 'Select a date first';
          placeholder.disabled = true;
          placeholder.selected = true;
          select.appendChild(placeholder);
          select.disabled = true;
          select.value = '';
          return;
        }
        options = buildTimeOptionsForDate(
          selectedDate,
          cfg.windows,
          cfg.format,
          cfg,
        );
      } else {
        options = (cfg.options || []).map((t) => ({
          value: t,
          label: t,
          group: '',
        }));
      }

      select.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = cfg.placeholder;
      placeholder.disabled = true;
      placeholder.selected = true;
      select.appendChild(placeholder);

      if (!options.length) {
        select.disabled = true;
        return;
      }

      select.disabled = false;

      // Group by window label when present.
      const groups = new Map();
      options.forEach((opt) => {
        const key = opt.group || '';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(opt);
      });

      groups.forEach((opts, groupLabel) => {
        let parent = select;
        if (groupLabel) {
          const og = document.createElement('optgroup');
          og.label = groupLabel;
          select.appendChild(og);
          parent = og;
        }
        opts.forEach((opt) => {
          const el = document.createElement('option');
          el.value = opt.value;
          el.textContent = opt.label;
          parent.appendChild(el);
        });
      });

      if (previousValue && options.some((o) => o.value === previousValue)) {
        select.value = previousValue;
        placeholder.selected = false;
      } else {
        select.value = '';
        placeholder.selected = true;
      }
    }

    /** Determine whether the form is submittable. */
    _isValid() {
      if (this.selections.length === 0) return false;
      for (const sel of this.selections) {
        const offering = this.offerings.find((o) => o.id === sel.offeringId);
        if (!offering) return false;
        if (offering.options.length > 0) {
          for (let i = 0; i < sel.boxSize; i++) {
            if (!sel.optionValues[i]) return false;
          }
        }
        if (
          offering.enableSpecialPackaging &&
          sel.specialPackagingRequested &&
          !String(sel.specialPackagingInstructions || '').trim()
        ) {
          return false;
        }
      }
      if (this.datepickerConfig) {
        if (!this._datepickerInput || !this._datepickerInput.value) {
          return false;
        }
        if (this._pickupTimeSelect && !this._pickupTimeSelect.value) {
          return false;
        }
      }
      const name = this.els.nameInput && this.els.nameInput.value.trim();
      if (!name) return false;
      const email = this.els.emailInput && this.els.emailInput.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
      if (!this._pickupLocationSelect || !this._pickupLocationSelect.value) {
        return false;
      }
      const phone = this.els.phoneInput && this.els.phoneInput.value.trim();
      if (!phone) return false;
      return true;
    }

    _refreshState() {
      // Update available list disabled flags + per-item full state
      this.els.availableList
        .querySelectorAll('.curated-form__available-item')
        .forEach((li) => {
          const offering = this.offerings.find(
            (o) => o.id === li.dataset.offeringId,
          );
          if (!offering) return;
          const full = this._isOfferingFull(offering);
          li.dataset.disabled = full ? 'true' : 'false';
          const cb = li.querySelector('input[type="checkbox"]');
          if (cb) {
            cb.disabled = full;
            if (full) {
              cb.checked = false;
              this.checkedOfferingIds.delete(offering.id);
            }
          }
        });

      this.els.addButton.disabled = this.checkedOfferingIds.size === 0;
      this.els.submit.disabled = !this._isValid();
      this._updateSubtotal();

      // Sync storeify form to the current valid/invalid state.
      this._syncStoreifyState();
    }

    /**
     * When the curated form is valid: populate the storeify fields, swap the
     * storeify wrapper class from `storeify-formbuilder-hidden` to
     * `storeify-formbuilder-ready`, and update our `data-state` so CSS can
     * hide our own submit button and reveal the storeify submit instead.
     */
    _syncStoreifyState() {
      const wrapper = this.els.storeify;
      if (!wrapper) return;

      const valid = this._isValid();
      const ready = valid && !!wrapper.querySelector('.storeify-submit-btn');

      if (ready) {
        wrapper.classList.remove('storeify-formbuilder-hidden');
        wrapper.classList.add('storeify-formbuilder-ready');
        this._populateStoreifyFields();
        this._setState('ready');
      } else {
        wrapper.classList.add('storeify-formbuilder-hidden');
        wrapper.classList.remove('storeify-formbuilder-ready');
        this._setState('editing');
      }
    }

    _populateStoreifyFields() {
      const wrapper = this.els.storeify;
      if (!wrapper) return;
      const nameField = wrapper.querySelector('.js-order-name');
      const emailField = wrapper.querySelector('.js-order-email');
      const pickupLocationField = wrapper.querySelector('.js-order-pickup-location');
      const phoneField = wrapper.querySelector('.js-order-phone-number');
      const pickupField = wrapper.querySelector('.js-order-pickup-date');
      const detailsField = wrapper.querySelector('.storeify-textarea[placeholder="Order Details"]');

      if (this.els.nameInput) {
        this._setFieldValue(nameField, this.els.nameInput.value.trim());
      }
      if (this.els.emailInput) {
        this._setFieldValue(emailField, this.els.emailInput.value.trim());
      }
      if (this._pickupLocationSelect) {
        this._setFieldValue(pickupLocationField, this._pickupLocationSelect.value);
      }
      if (this.els.phoneInput) {
        this._setFieldValue(phoneField, this.els.phoneInput.value.trim());
      }
      if (this._datepickerInput) {
        this._setFieldValue(pickupField, this._buildPickupDateTime());
      }
      this._setFieldValue(detailsField, this._buildOrderDetails());
    }

    _setState(state) {
      if (this._state === state) return;
      this._state = state;
      this.dataset.state = state;
    }

    /**
     * Combine the picked date with the optional pickup time into a single
     * freeform string for the Storeify `js-order-pickup-date` field.
     */
    _buildPickupDateTime() {
      const date = this._datepickerInput ? this._datepickerInput.value : '';
      const time = this._pickupTimeSelect ? this._pickupTimeSelect.value : '';
      if (date && time) return `${date} at ${time}`;
      return date;
    }

    /** Build the structured order-details string for the storeify textarea. */
    _buildOrderDetails() {
      const lines = [];
      let subtotal = 0;
      this.selections.forEach((sel, idx) => {
        const offering = this.offerings.find((o) => o.id === sel.offeringId);
        const name = offering ? offering.name : sel.offeringId;
        const box = offering && offering.boxes.find((b) => b.size === sel.boxSize);
        const sizeLabel = box && box.label ? box.label : String(sel.boxSize);
        lines.push(`${idx + 1}. ${name}`);
        lines.push(`   Size: ${sizeLabel}`);
        if (sel.optionValues.length) {
          sel.optionValues.forEach((v, i) => {
            lines.push(`   Option ${i + 1}: ${v || '-'}`);
          });
        }
        if (offering && offering.enableSpecialPackaging && sel.specialPackagingRequested) {
          lines.push('   Special packaging requested: Yes');
          if ((offering.specialPackagingPrice || 0) > 0) {
            lines.push(
              `   Special packaging price (per item): ${formatDollarAmount(
                offering.specialPackagingPrice,
              )}`,
            );
          }
          lines.push(
            `   Special packaging instructions: ${
              String(sel.specialPackagingInstructions || '').trim() || '-'
            }`,
          );
        }
        const unitPrice = this._selectionUnitPrice(sel, offering);
        const lineTotal = this._selectionLineTotal(sel, offering);
        subtotal += lineTotal;
        lines.push(`   Unit price: ${formatDollarAmount(unitPrice)}`);
        lines.push(`   Line total: ${formatDollarAmount(lineTotal)}`);
        lines.push(`   Quantity: ${sel.qty}`);
        lines.push('');
      });

      if (this.selections.length) {
        lines.push('---');
        lines.push(`Subtotal: ${formatDollarAmount(subtotal)}`);
      }

      return lines.join('\n').trimEnd();
    }

    /** Set a value on a form control and dispatch input/change events. */
    _setFieldValue(el, value) {
      if (!el) return;
      el.value = value;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _onSubmit(e) {
      e.preventDefault();

      if (!this._isValid()) {
        this.els.error.textContent =
          'Please add at least one item, complete all option selections, choose a pickup location, choose a pickup date and time, and enter your name, email, and phone number.';
        return;
      }
      this.els.error.textContent = '';

      const wrapper = this.els.storeify;
      const submitBtn = wrapper && wrapper.querySelector('.storeify-submit-btn');
      if (!wrapper || !submitBtn) {
        this.els.error.textContent =
          'Submission form is not ready. Please try again in a moment.';
        return;
      }

      // Live-sync already populates the storeify fields, but re-run here in case
      // an input event was missed (e.g. autofill).
      this._populateStoreifyFields();
      submitBtn.click();
    }
  }

  if (!customElements.get('curated-form')) {
    customElements.define('curated-form', CuratedForm);
  }
})();
